/**
 * Scrolls to the specified section with smooth behavior
 * @param {string} sectionId - The ID of the section to scroll to
 * @param {number} offset - Offset from the top (default: 70px)
 */
export const scrollToSection = (sectionId, offset = 70) => {
  if (typeof window === 'undefined') return;

  const element = document.getElementById(sectionId);
  if (element) {
    window.scrollTo({
      top: element.offsetTop - offset,
      behavior: 'smooth',
    });
  }
};

/**
 * Scrolls a section into view honoring its CSS `scroll-margin-top`.
 *
 * Prefer this over `scrollToSection` for new code. That one subtracts a
 * hardcoded 70px, which duplicates — and disagrees with — the
 * `scroll-margin-top: clamp(7rem, 6vw, 8rem)` that pages/Home.jsx already
 * declares on every section for exactly this purpose. Letting the browser apply
 * it keeps the sticky-header offset in one place. `scrollToSection` is left
 * alone because the nav, hero, and closed banner are all calibrated to it.
 *
 * Honors `prefers-reduced-motion`: a smooth scroll across the whole page is
 * precisely the kind of large motion that setting exists to suppress.
 *
 * @param {string} sectionId - The ID of the section to scroll to
 * @returns {boolean} whether the section was found
 */
export const scrollSectionIntoView = (sectionId) => {
  if (typeof document === 'undefined') return false;

  const element = document.getElementById(sectionId);
  if (!element) return false;

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  element.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });

  return true;
};

/**
 * Returns the active section based on current scroll position
 * @param {string[]} sectionIds - Array of section IDs to check
 * @param {number} threshold - Position threshold (default: 100px)
 * @returns {string} - The ID of the active section
 */
export const getActiveSection = (sectionIds, threshold = 100) => {
  if (typeof window === 'undefined' || !sectionIds || sectionIds.length === 0) {
    return '';
  }

  for (const section of sectionIds) {
    const element = document.getElementById(section);
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect.top <= threshold && rect.bottom >= threshold) {
        return section;
      }
    }
  }
  return sectionIds[0]; // Default to first section if none found
};

/**
 * Creates a scroll event handler that updates the active section
 * @param {string[]} sectionIds - Array of section IDs to monitor
 * @param {function} setActiveSection - State setter function for active section
 * @returns {Object} - Object containing setup and cleanup functions
 */
export const createScrollSpy = (sectionIds, setActiveSection) => {
  if (typeof window === 'undefined' || !sectionIds || !setActiveSection) {
    return {
      setup: () => {},
      cleanup: () => {},
    };
  }

  let scrollTimer = null;

  const handleScroll = () => {
    // Debounce scroll events
    if (scrollTimer) clearTimeout(scrollTimer);

    scrollTimer = setTimeout(() => {
      const newActiveSection = getActiveSection(sectionIds);
      if (newActiveSection) {
        setActiveSection(newActiveSection);
      }
    }, 100);
  };

  const setup = () => {
    window.addEventListener('scroll', handleScroll);
    // Initial call to set active section on mount
    handleScroll();
  };

  const cleanup = () => {
    if (scrollTimer) clearTimeout(scrollTimer);
    window.removeEventListener('scroll', handleScroll);
  };

  return { setup, cleanup };
};
